<template>
  <div class="file-viewer" :style="containerStyle">
    <NotificationBar
      v-if="error"
      severity="error"
      :mainText="error"
      icon="mdi mdi-file-question"
      class="m-4"
      :showCloseButton="false"
    />

    <!-- Inline image -->
    <img
      v-else-if="!loading && previewKind === 'image' && blobUrl"
      :src="blobUrl"
      class="max-w-full h-auto block"
      alt=""
    />

    <!-- Inline PDF or text via iframe -->
    <iframe
      v-else-if="!loading && (previewKind === 'pdf' || previewKind === 'text') && blobUrl"
      :src="blobUrl"
      class="w-full h-[80vh] border-0"
    ></iframe>

    <iframe v-else-if="!loading && blobUrl" :src="blobUrl" class="w-full h-screen"></iframe>

    <div v-else-if="loading">
      <p>{{ t('messages.loadingPDF') }} ...</p>
    </div>

    <!-- Unsupported inline: show manual download button -->
    <div class="p-4 flex items-center gap-3">
      <div class="flex-1">
        <NotificationBar
          v-if="previewKind === 'unsupported' && !loading"
          :mainText="$t('messages.noInlinePreview')"
          icon="pi pi-eye-slash"
          :showCloseButton="false"
        />
        <!-- <i class="pi pi-file"></i> -->
        <Button
          v-if="!error && !loading"
          icon="pi pi-download"
          class="mt-2"
          :label="
            props.filename
              ? $t('buttons.downloadFileWithName', { name: props.filename })
              : $t('buttons.downloadFile')
          "
          @click="downloadManually"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { getPreviewKind, type PreviewKind } from '@/utils/files';
import { DocumentsService } from '@/openapi/services/DocumentsService';

const { t } = useI18n();
const props = withDefaults(
  defineProps<{
    uuid: string;
    mime?: string;
    filename?: string;
    allowText?: boolean;
    allowCsvAsText?: boolean;
    initialHeight?: string;
  }>(),
  {
    mime: undefined,
    filename: undefined,
    initialHeight: '1000px',
  },
);

const blobValue = ref<Blob | null>(null);
const blobUrl = ref('');
const loading = ref(false);
const error = ref('');
const previewKind = ref<PreviewKind>('unsupported');

const isBlobLoaded = computed(() => !!blobUrl.value);

/** Height logic based purely on status (blob/error/unsupported/loading). */
const containerStyle = computed(() => {
  // Error or unsupported preview -> auto (no need for reserved space)
  if (error.value) return { height: 'auto' };
  if (!loading.value && previewKind.value === 'unsupported') return { height: 'auto' };

  // Blob fetched -> we can release reserved height
  if (isBlobLoaded.value) return { height: 'auto' };

  // Otherwise (initial / loading / pre-fetch) keep reserved height
  return { height: props.initialHeight };
});

watch(
  () => [props.uuid, props.mime],
  async ([uuid, mime]) => {
    // Only fetch blob for inline-previewable types
    if (uuid) {
      await loadBlobForPreview(uuid, mime);
    } else {
      // prevent auto-download for unsupported: do nothing
      revokeUrl();
    }
  },
  { immediate: true },
);

async function loadBlobForPreview(uuid: string, mime?: string) {
  loading.value = true;
  error.value = '';
  revokeUrl();

  try {
    const blob = await DocumentsService.documentControllerV1DownloadDocumentV1(uuid);
    // Rely on server content-type so the browser knows how to render
    blobValue.value = blob;

    previewKind.value = await getPreviewKind(
      mime,
      {
        allowText: props.allowText,
        allowCsvAsText: props.allowCsvAsText,
      },
      blob,
    );

    if (previewKind.value !== 'unsupported') {
      // Convert octet-stream to PDF for preview
      let blobForPreview = blob;
      if (mime === 'application/octet-stream' && previewKind.value === 'pdf') {
        blobForPreview = new Blob([blob], { type: 'application/pdf' });
      }
      blobUrl.value = URL.createObjectURL(blobForPreview);
    }
  } catch (err: any) {
    if (err?.response?.status === 404) {
      error.value = t('toast.errors.FILE_NOT_FOUND');
    } else {
      error.value = t('toast.errors.FILE_DOWNLOAD_ERROR');
    }
  } finally {
    loading.value = false;
  }
}

function revokeUrl() {
  if (blobUrl.value) {
    URL.revokeObjectURL(blobUrl.value);
    blobUrl.value = '';
  }
}

/** Manual download on user action only (no auto-download). */
async function downloadManually() {
  try {
    loading.value = true;
    const blob = await DocumentsService.documentControllerV1DownloadDocumentV1(props.uuid);
    const url = URL.createObjectURL(blob);
    // trigger a download via temporary anchor
    const a = document.createElement('a');
    a.href = url;
    a.download = props.filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (_err) {
    error.value = t('toast.errors.FILE_DOWNLOAD_ERROR');
  } finally {
    loading.value = false;
  }
}

onUnmounted(revokeUrl);
</script>

<style lang="css">
@layer app {
  iframe {
    border: none;
  }

  .file-viewer {
    position: relative;
    display: block;
    overflow: auto;
  }
}
</style>
